import React, { useEffect, useState } from 'react';
import { FiAlertCircle, FiX } from 'react-icons/fi';
import { useConnectionBrokenAlertStore } from '../../stores';
import { useApi } from '../../hooks/useApi';
import { brokerageApi } from '../../api';
import { useAuthStore } from '../../stores';

const ConnectionBrokenAlert: React.FC = () => {
  const { alert, setAlert, clearAlert } = useConnectionBrokenAlertStore();
  const [isVisible, setIsVisible] = useState(false);
  const { user } = useAuthStore();
  const dismissAlertMutation = useApi.useDismissAlert();
  const { data: alertStatus } = useApi.useAlertStatus(alert?.alertId || null);
  const { data: activeAlertsData, refetch: refetchActiveAlerts } = useApi.useActiveAlerts({
    enabled: !!user, // Only fetch when user is logged in
  });

  // Fetch active alerts on mount and when user changes
  useEffect(() => {
    if (user && activeAlertsData?.alerts && activeAlertsData.alerts.length > 0) {
      // Get the first active alert (most recent)
      const firstAlert = activeAlertsData.alerts[0];
      
      // Only set if we don't already have an alert or if it's a different alert
      const currentAlert = useConnectionBrokenAlertStore.getState().alert;
      if (!currentAlert || currentAlert.alertId !== firstAlert._id) {
        // Extract user info if populated
        const affectedUserId = (firstAlert.affectedUserId && typeof firstAlert.affectedUserId === 'object' && firstAlert.affectedUserId !== null)
          ? firstAlert.affectedUserId._id 
          : firstAlert.affectedUserId;
        const affectedUserName = (firstAlert.affectedUserId && typeof firstAlert.affectedUserId === 'object' && firstAlert.affectedUserId !== null)
          ? firstAlert.affectedUserId.name 
          : '';
        const brokerageConnectionId = (firstAlert.brokerageConnectionId && typeof firstAlert.brokerageConnectionId === 'object' && firstAlert.brokerageConnectionId !== null)
          ? firstAlert.brokerageConnectionId._id
          : firstAlert.brokerageConnectionId;
        const brokerageName = (firstAlert.brokerageConnectionId && typeof firstAlert.brokerageConnectionId === 'object' && firstAlert.brokerageConnectionId !== null)
          ? firstAlert.brokerageConnectionId.brokerageName || firstAlert.brokerageName
          : firstAlert.brokerageName;

        setAlert({
          affectedUserId: affectedUserId,
          affectedUserName: affectedUserName,
          brokerageConnectionId: brokerageConnectionId,
          brokerageAuthorizationId: firstAlert.brokerageAuthorizationId,
          brokerageName: brokerageName,
          alertId: firstAlert._id
        });
        setIsVisible(true);
      }
    }
  }, [user, activeAlertsData, setAlert]);

  useEffect(() => {
    const handleConnectionBroken = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const data = customEvent.detail;
      const alertId = data.alertId;
      
      // Check if this alert has been dismissed by checking API
      try {
        const statusData = await brokerageApi.getAlertStatus(alertId);
        if (statusData.isDismissed) {
          console.log('Alert already dismissed, not showing:', alertId);
          return;
        }
      } catch (error) {
        console.error('Error checking alert status:', error);
        // Continue to show alert if check fails
      }

      setAlert({
        affectedUserId: data.affectedUserId,
        affectedUserName: data.affectedUserName,
        brokerageConnectionId: data.brokerageConnectionId,
        brokerageAuthorizationId: data.brokerageAuthorizationId,
        brokerageName: data.brokerageName,
        alertId: alertId
      });
      setIsVisible(true);
      
      // Refetch active alerts to sync
      refetchActiveAlerts();
    };

    const handleConnectionFixed = (event: Event) => {
      const customEvent = event as CustomEvent;
      const data = customEvent.detail;
      // Get current alert from store to check if it matches
      const currentAlert = useConnectionBrokenAlertStore.getState().alert;
      if (currentAlert && currentAlert.brokerageAuthorizationId === data.brokerageAuthorizationId) {
        setIsVisible(false);
        setTimeout(() => {
          clearAlert();
        }, 300); // Wait for fade out animation
      }
      // Refetch active alerts to sync
      refetchActiveAlerts();
    };

    window.addEventListener('connection:broken', handleConnectionBroken);
    window.addEventListener('connection:fixed', handleConnectionFixed);

    return () => {
      window.removeEventListener('connection:broken', handleConnectionBroken);
      window.removeEventListener('connection:fixed', handleConnectionFixed);
    };
  }, [setAlert, clearAlert, refetchActiveAlerts]);

  // Check dismissed status when alert changes
  useEffect(() => {
    if (alert && alertStatus) {
      if (alertStatus.isDismissed) {
        // Alert is dismissed, don't show it
        setIsVisible(false);
        clearAlert();
      } else {
        setIsVisible(true);
      }
    } else if (alert && !alertStatus) {
      // Alert exists but status not loaded yet, show it
      setIsVisible(true);
    }
  }, [alert, alertStatus, clearAlert]);

  const handleDismiss = async () => {
    if (!alert?.alertId) return;

    try {
      await dismissAlertMutation.mutateAsync(alert.alertId);
      setIsVisible(false);
      setTimeout(() => {
        clearAlert();
        // Refetch active alerts to check if there are more
        refetchActiveAlerts();
      }, 300);
    } catch (error) {
      console.error('Error dismissing alert:', error);
      // Still hide the alert even if API call fails
      setIsVisible(false);
      setTimeout(() => {
        clearAlert();
        refetchActiveAlerts();
      }, 300);
    }
  };

  if (!alert || !isVisible) {
    return null;
  }

  // Check if current user is admin
  const isAdmin = user?.role === 'admin';
  
  // Check if this alert is for the current user or for another user
  const currentUserId = user?._id?.toString() || user?.id?.toString() || '';
  let affectedUserId = '';
  if (alert?.affectedUserId) {
    if (typeof alert.affectedUserId === 'string') {
      affectedUserId = alert.affectedUserId;
    } else {
      affectedUserId = String(alert.affectedUserId);
    }
  }
  const isOwnAlert = currentUserId && affectedUserId && currentUserId === affectedUserId;

  // Determine message based on user role and alert ownership
  let title = 'Brokerage Connection Broken';
  let isAdminViewingOtherUser = false;
  
  if (isAdmin && !isOwnAlert) {
    // Admin viewing another user's broken connection
    title = 'User Connection Broken';
    isAdminViewingOtherUser = true;
  }

  return (
    <div
      className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
      }`}
      style={{ maxWidth: '90%', width: '600px' }}
    >
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg shadow-lg p-4 flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <FiAlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">
            {title}
          </h3>
          <p className="text-sm text-red-700 dark:text-red-300">
            {isAdminViewingOtherUser ? (
              <>
                <strong>{alert.affectedUserName || 'A user'}</strong>'s connection to <strong>{alert.brokerageName}</strong> is broken. Please fix it.
              </>
            ) : (
              <>
                Your connection to <strong>{alert.brokerageName}</strong> is broken. Please disconnect your brokerage and connect it again, otherwise contact admin.
              </>
            )}
          </p>
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 transition-colors"
          aria-label="Dismiss alert"
        >
          <FiX className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default ConnectionBrokenAlert;
